import io from 'socket.io-client'

import { setData } from 'ducks/editor/objects'

import {
  loadApp,
  loadAppsList,
  loadOrgAppsList,
  loadTemplatesList,
  loadTemplateBranding,
  loadAppDatasourceRelationships,
} from 'ducks/apps'

import {
  loadData,
  displayError,
  loadAssociationData,
  deleteData,
  bulkDeleteData,
  loadSearch,
} from 'ducks/datasources'

import {
  setCurrentUser,
  setCurrentUserToken,
  setAuthVisible,
} from 'ducks/users'

import { socketConnected, socketDisconnected } from 'ducks/sockets'
import { uploadProgress, uploadError } from 'ducks/editor/sketchUpload'
import { TWO_FACTOR_MODAL, showModal } from 'ducks/editor/modals'

import { getAuthToken } from 'utils/auth'
import { getIncludes } from 'utils/includes'
import { defaultBranding } from 'utils/colors'

import {
  loadScreenTemplates,
  loadScreenTemplateCategories,
  screenTemplateCreated,
  screenTemplateUpdated,
} from 'ducks/editor/screenTemplates'
import { loadScreenAppBuilds } from 'ducks/apps/builds'
import { socket } from 'utils/io/socket'
import history from '../../history'

// define store
let reduxStore = null

const saveQueue = {}

socket.on('reconnect_attempt', () => {
  socket.io.opts.query = {
    sessionToken: getAuthToken(),
  }
})

export const unsafeGetStore = () => reduxStore

export const connectSocket = store => {
  const token = getAuthToken()

  reduxStore = store

  if (token) {
    store.dispatch(setCurrentUserToken(token))
  } else {
    store.dispatch(setAuthVisible())
  }

  socket.on('connect', () => {
    store.dispatch(socketConnected())
  })

  socket.on('disconnect', () => {
    store.dispatch(socketDisconnected())
  })

  socket.on('connect_error', () => {
    store.dispatch(socketDisconnected())
  })

  socket.on('app', result => {
    const state = store.getState()
    const { appId } = state.editor.objects.present

    if (!result) return null

    if (appId && appId === result.id) {
      store.dispatch(setData(appId, result.components, result))
    }

    if (result.associatedApps) {
      result.associatedApps.forEach(app => {
        if (!(app.id in state.apps.apps)) {
          requestApp(app.id)
        }
      })
    }

    if (result.created) {
      history.push(`/apps/${result.id}`)
    }

    store.dispatch(loadApp(result))

    socket.emit('requestAppDatasourceRelationships', { appId })
  })

  socket.on('appNotFound', () => {
    history.push('/')
  })

  socket.on('appsList', (apps, lastApp) => {
    store.dispatch(loadAppsList(apps, lastApp))
  })

  socket.on('orgAppsList', (apps, lastApp) => {
    store.dispatch(loadOrgAppsList(apps, lastApp))
  })

  socket.on('templatesList', (templateList, primaryPlatform) => {
    store.dispatch(loadTemplatesList(templateList, primaryPlatform))
  })

  socket.on('templateBranding', (templateId, branding) => {
    store.dispatch(loadTemplateBranding(templateId, branding))
  })

  socket.on('appDatasourceRelationships', ({ appId, relationships }) => {
    store.dispatch(loadAppDatasourceRelationships(appId, relationships))
  })

  socket.on('screenTemplatesList', (platform, list) => {
    console.log('GOT SCREEN TEMPLATES', platform, list)
    store.dispatch(loadScreenTemplates(platform, list))
  })

  socket.on('screenTemplateCategoriesList', (platform, list) => {
    store.dispatch(loadScreenTemplateCategories(platform, list))
  })

  socket.on('screenTemplateCreated', (platform, template) => {
    store.dispatch(screenTemplateCreated(platform, template))
  })

  socket.on('screenTemplateUpdated', (platform, template) => {
    store.dispatch(screenTemplateUpdated(platform, template))
  })

  socket.on('appBuilds', (appId, target, list) => {
    store.dispatch(loadScreenAppBuilds(appId, target, list))
  })

  socket.on('data', ({ tableId, result, error }) => {
    if (error) return store.dispatch(displayError(error))
    store.dispatch(loadData(tableId, result))
    store.dispatch(displayError(null))
  })

  socket.on('dataSearch', ({ tableId, result, error }) => {
    if (error) return store.dispatch(displayError(error))
    store.dispatch(loadSearch(tableId, result))
    store.dispatch(displayError(null))
  })

  socket.on('associationData', ({ fieldId, result }) => {
    store.dispatch(loadAssociationData(fieldId, result))
  })

  socket.on('dataDeleted', ({ tableId, id }) => {
    store.dispatch(deleteData(tableId, id))
  })

  socket.on('bulkDataDeleted', ({ tableId, blockedList, idList }) => {
    store.dispatch(bulkDeleteData(tableId, blockedList, idList))
  })

  socket.on('userProfile', user => {
    store.dispatch(setCurrentUser(user))
  })

  socket.on('unauthorized', () => {
    console.log('SETTING AUTH VISIBLE....')
    store.dispatch(setAuthVisible())
  })

  socket.on('uploadProgress', progressObj => {
    store.dispatch(uploadProgress(progressObj))
  })

  socket.on('uploadError', () => {
    window.alert(
      'We were unable to upload your Sketch document. Please make sure it was produced with a recent version of Sketch (45+) and is less than 100MB in size.'
    )

    store.dispatch(uploadError())
  })

  socket.on('requestApple2FA', async callback => {
    try {
      const result = await store.dispatch(showModal(TWO_FACTOR_MODAL))
      callback({ code: result.value })
    } catch (err) {
      callback({ error: 'Cancelled' })
    }
  })
}

// Change Queue
window.setInterval(() => {
  forceSaveComponents()
}, 30000)

const SAVE_TIMEOUT_MILLISECONDS = 30000

const waitForEvent = eventName => {
  return new Promise((resolve, reject) => {
    const onEvent = ({ success, err }) => {
      clearTimeout(timeoutId)

      if (success) {
        resolve()
      } else {
        reject(new Error(`Save failed: ${err}`))
      }
    }

    const clearEverything = () => {
      clearTimeout(timeoutId)
      socket.off(eventName, onEvent)
    }

    const onTimeOut = () => {
      clearEverything()
      reject(new Error('TIMED OUT'))
    }

    const timeoutId = setTimeout(onTimeOut, SAVE_TIMEOUT_MILLISECONDS)

    socket.once(eventName, onEvent)
  })
}

export const forceSaveComponents = (onSaveFinish = undefined) => {
  const pendingSaves = []

  for (const appId of Object.keys(saveQueue)) {
    const changes = saveQueue[appId]

    // Clear out previous values
    delete saveQueue[appId]

    const { components, libraryGlobals } = changes

    if (!components && !libraryGlobals) {
      continue
    }

    const pendingSave = waitForEvent(`savedComponents:${appId}`)

    pendingSaves.push(pendingSave)

    socket.emit('saveComponent', {
      appId,
      components,
      libraryGlobals,
    })
  }

  if (onSaveFinish && typeof onSaveFinish === 'function') {
    Promise.all(pendingSaves) //
      .then(() => onSaveFinish())
      .catch(err => onSaveFinish(err))
  } else {
    // Nobody cares about the result so ignore errors (probably a timeout which is irrelevant when there's no callback)
    Promise.all(pendingSaves).catch(() => {})
  }
}

const addSavesToQueue = (appId, components, libraryGlobals) => {
  saveQueue[appId] = {
    components: {
      ...saveQueue[appId]?.components,
      ...components,
    },
    libraryGlobals: {
      ...saveQueue[appId]?.libraryGlobals,
      ...libraryGlobals,
    },
  }
}

export const requestAll = () => {
  socket.emit('requestAll')
}

export const requestAllOrgApps = orgId => {
  socket.emit('requestAllOrgApps', { orgId })
}

export const requestApp = appId => {
  socket.emit('requestApp', { appId })
}

export const createApp = (opts, callback) => {
  const {
    name,
    datasourceType,
    datasourceAppId,
    organizationId,
    primaryPlatform,
    templateId,
    prototypeAppId,
    copyApp,
    cloneAppWithData,
    copyDatabase,
  } = opts

  let fonts = {
    body: {
      family: 'default',
    },
    heading: {
      family: 'Oswald',
      variants: ['200', '300', 'regular', '500', '600', '700'],
      category: 'sans-serif',
    },
  }

  if ((copyApp || cloneAppWithData || copyDatabase) && opts.branding.fonts) {
    fonts = opts.branding.fonts
  }

  const branding = {
    ...defaultBranding,
    ...opts.branding,
    fonts,
  }

  const ioOpts = {
    name,
    datasourceType,
    datasourceAppId,
    primaryPlatform,
    organizationId,
    branding,
    copyApp,
    cloneAppWithData,
  }

  if (templateId && templateId !== 'blank') {
    ioOpts.templateId = templateId
  } else {
    if (prototypeAppId) {
      ioOpts.prototypeAppId = prototypeAppId
    } else if (datasourceType === 'database' && primaryPlatform === 'web') {
      ioOpts.prototypeAppId = process.env.REACT_APP_PROTOTYPE_APP_ID_WEB
    } else if (datasourceType === 'database' && primaryPlatform === 'mobile') {
      ioOpts.prototypeAppId = process.env.REACT_APP_PROTOTYPE_APP_ID
    }
  }

  return new Promise((resolve, reject) => {
    socket.emit('createApp', ioOpts, errorCreatingApp => {
      if (callback) {
        callback(errorCreatingApp)
      }

      resolve()
    })
  })
}

export const updateApp = (appId, data) => {
  socket.emit('updateApp', { appId, data })
}

export const deleteApp = appId => {
  socket.emit('deleteApp', { appId })
}

export const saveComponent = (appId, componentId, data) => {
  socket.emit('saveComponent', { appId, componentId, data })
}

export const saveComponents = (appId, components, libraryGlobals) => {
  //socket.emit('saveComponent', { appId, components, libraryGlobals })
  addSavesToQueue(appId, components, libraryGlobals)
}

export const deleteComponent = (appId, componentId) => {
  if (saveQueue[appId]?.components[componentId]) {
    delete saveQueue[appId].components[componentId]
  }

  socket.emit('deleteComponent', { appId, componentId })
}

export const saveLibraryGlobals = (appId, libraryGlobals) => {
  socket.emit('saveLibraryGlobals', appId, libraryGlobals)
}

export const saveDatasource = (
  appId,
  datasourceId,
  data,
  sendUpdate = false
) => {
  socket.emit(
    'saveDatasource',
    { appId, datasourceId, data, sendUpdate },
    () => {
      socket.emit('requestAppDatasourceRelationships', { appId })
    }
  )
}

export const uploadSketchFile = (appId, file, fileType, callback) => {
  console.log('FILE TYPE:', fileType)

  socket.emit('uploadSketchFile', { appId, file, fileType }, callback)
}

export const setParams = opts => {
  socket.emit('setParams', opts)
}

// ARGS = {
//   appId,
//   datasourceId,
//   tableId,
//   [id],
//   [tableConfig],
// }

/**
 * Requests data
 * @param {*} args
 * { appId, datasourceId, tableId, [id], [tableConfig], queryParams }
 */
export const requestData = async (args = {}, sendCallback = false) => {
  let { tableConfig, ...opts } = args

  let queryParams = {}
  if (opts.queryParams) queryParams = opts.queryParams
  if (tableConfig) queryParams.include = getIncludes(tableConfig)

  opts = {
    ...opts,
    queryParams,
  }

  return new Promise((resolve, reject) => {
    const callback = sendCallback ? resolve : undefined

    socket.emit('requestData', opts, callback)

    if (!sendCallback) {
      resolve()
    }
  })
}

// Used to create a Table data object
export const saveDataObject = (appId, datasourceId, tableId, id, data) => {
  socket.emit('saveDataObject', { appId, datasourceId, tableId, id, data })
}

export const deleteDataObject = (appId, datasourceId, tableId, id) => {
  socket.emit('deleteDataObject', { appId, datasourceId, tableId, id })
}

export const bulkDeleteDataObjects = (
  appId,
  datasourceId,
  tableId,
  blockedList,
  idList,
  queryParams
) => {
  socket.emit('bulkDeleteDataObjects', {
    appId,
    datasourceId,
    tableId,
    blockedList,
    idList,
    queryParams,
  })
}

// opts = { appId, datasourceId, tableId, id, tableId2, id2, fieldId }
export const addAssociation = opts => {
  socket.emit('addAssociation', opts)
}

// opts = { appId, datasourceId, tableId, id, tableId2, id2, fieldId }
export const removeAssociation = opts => {
  socket.emit('removeAssociation', opts)
}

export const authenticate = (data, callback) => {
  return new Promise((resolve, reject) => {
    socket.emit('authenticate', data, ({ success, sessionToken }) => {
      if (success) {
        resolve(sessionToken)
      } else {
        reject(new Error('Invalid email or password'))
      }
    })
  })
}

export const passwordRecovery = (data, callback) => {
  socket.emit('passwordRecovery', data, callback)
}

export const updatePassword = (data, callback) => {
  socket.emit('updatePassword', data, callback)
}

export const signup = (data, callback) => {
  socket.emit('signup', data, callback)
}

export const uploadImage = (appId, file, filename) =>
  new Promise((resolve, reject) => {
    socket.emit('uploadImage', { appId, file, filename }, result => {
      if (result.success) {
        resolve(result.filename)
      } else {
        reject(new Error('Unable to upload asset'))
      }
    })
  })

export const shareSMS = (opts, callback) => {
  socket.emit('shareSMS', { ...opts, isFoundry: true }, callback)
}

export const updateAccount = data => {
  return new Promise((resolve, reject) => {
    socket.emit('updateAccount', data, result => {
      if (result && result.success) {
        resolve(result)
      } else {
        reject(result)
      }
    })
  })
}

export const getUUID = () => {
  return new Promise((resolve, reject) => {
    socket.emit('getUUID', result => {
      resolve(result)
    })
  })
}

export const requestTemplates = primaryPlatform => {
  socket.emit('requestAllTemplates', primaryPlatform)
}

export const requestTemplateBranding = templateId => {
  socket.emit('requestTemplateBranding', templateId)
}

export const requestScreenTemplates = (platform = 'mobile') => {
  socket.emit('requestScreenTemplates', platform)
}

export const requestScreenTemplateCategories = (platform = 'mobile') => {
  socket.emit('requestScreenTemplateCategories', platform)
}

export const createOrUpdateScreenTemplate = (appId, componentId, values) => {
  return new Promise((resolve, reject) => {
    const callback = (error, id) => {
      if (error) {
        reject(error)
      } else {
        resolve(id)
      }
    }

    socket.emit(
      'createOrUpdateScreenTemplate',
      appId,
      componentId,
      values,
      callback
    )

    socket.emit('createScreenTemplate', appId, componentId, values, callback)
  })
}

export const cloneScreenTemplatesToApp = (templateId, appId, screenName) => {
  return new Promise((resolve, reject) => {
    socket.emit(
      'cloneScreenTemplatesToApp',
      templateId,
      appId,
      screenName,
      (error, id) => {
        if (error) {
          reject(error)
        } else {
          resolve(id)
        }
      }
    )
  })
}

export const requestAppBuilds = (appId, target, opts = {}) => {
  socket.emit('requestAppBuilds', appId, target, opts)
}

export const requestStartAppBuild = (appId, target, version) => {
  forceSaveComponents()

  return new Promise((resolve, reject) => {
    socket.emit('startAppBuild', appId, target, version, (error, build) => {
      if (error) {
        reject(error)
      } else {
        resolve(build)
      }
    })
  })
}

export const verifyIOSCredentials = async appId =>
  new Promise((resolve, reject) => {
    socket.emit('verifyIOSCredentials', appId, (error, message) => {
      if (error) {
        reject(new Error(message))
      } else {
        resolve()
      }
    })
  })

// database socket
const databaseWSURL = process.env.REACT_APP_DATABASE_URL

export const databaseSocket = io(databaseWSURL, { transports: ['websocket'] })

export * from 'utils/io/versions'
export { baseURL } from 'utils/io/constants'
